import { useEffect, useMemo, useRef } from 'react'
import CloseButtonModal from '../../../../ui/close-button-modal'
import ViewSection from '../../../../ui/view-section'
import ChannelTypeReview from '../../../create-proposal/steps/fourth-step/review-proposal/channel-type-review'
import BasicInfoReview from '../../../create-proposal/steps/fourth-step/review-proposal/basic-info-review'
import { useDispatch, useSelector } from 'react-redux'
import { pa_clearProposeData, selectProposeAffiliation, selectProposeBasicInformation, selectProposeBasicPlans, selectProposeBorrowerAgeOptions, selectProposeChanelType, selectProposeCommissionValue, selectProposeCompanyMobileNumber, selectProposeCompanyNumber, selectProposeContactPersonMobileNumber, selectProposeContent, selectProposeCoverageType, selectProposeEligibilityAge, selectProposeFiles, selectProposeFundingArrangements, selectProposeGroupClassification, selectProposeInputsForLevelRanking, selectProposeLoanMaturityMonths, selectProposeLoanPortfolio, selectProposeMaxAge, selectProposeMaxAmount, selectProposeMinAge, selectProposeMinAmount, selectProposeNatureOfBusinessItem, selectProposeOtherSpecificationGC, selectProposeOtherSpecificationToG, selectProposePaymentTerm, selectProposeProductType, selectProposeProposalAs, selectProposeProposalType, selectProposePrototypeType, selectProposeRidersAmountOfLoans, selectProposeRidersForLevelRanking, selectProposeRidersForSalaryRank, selectProposeRidersForUniform, selectProposeRowsForSalaryRank, selectProposeSelectedAmountOfLoans, selectProposeSelectedBMSR, selectProposeSelectedRiders, selectProposeSelectedUniformCoverage, selectProposeServiceValue, selectProposeSubType, selectProposeTypeOfGroup } from '../../../../../features/propose-data/propose-data-slice'
import { CHANNEL_TYPE_ID, COVERAGE_TYPE_IDS, OTHERS_INPUT_IDS, PRODUCT_TYPE_IDS, PROPOSAL_TYPE_IDS, SUB_TYPE_IDS } from '../../../../../constant/gms-constant'
import Selection from '../../../create-proposal/steps/fourth-step/review-proposal/selection'
import { formatNumberWithComma } from '../../../../../utils/format-numbers.utils'
import LevelRanking from '../../../create-proposal/steps/fourth-step/review-proposal/level-ranking'
import UniformCoverage from '../../../create-proposal/steps/fourth-step/review-proposal/uniform-coverage'
import SalaryRank from '../../../create-proposal/steps/fourth-step/review-proposal/salary-rank'
import SelectedAttachableRiderTable from '../../../create-proposal/steps/fourth-step/review-proposal/selected-attachable-rider-table'
import ClientInputForBorrowers from '../../../create-proposal/steps/second-step/age-borrowers/client-input-for-borrowers'
import ClientSupportingDocuments from '../../../create-proposal/steps/third-step/client-supporting-documents'
import { setAmendHistoryId, setProposalIdAmendment, setSubAmendmentModal } from '../../../../../features/actuarial/request-amendment/request-amendment.slice'
import { useProposeData } from '../../../../../hooks/populate-data/use-propose-data'
import LoadingState from '../../../../ui/loadibg-state'
import ErrorState from '../../../../ui/error-state'

export default function ProposedProposalPreview() {

    const dispatch = useDispatch()
    const {
        proposal,
        isLoadingGetAmendmentHistoryLogs,
        isErrorGetAmendmentHistoryLogs,
        repopulateProposeAll,
    } = useProposeData();
    
    const handleCloseModal = () => {
        dispatch(setSubAmendmentModal(null))
        dispatch(setProposalIdAmendment(null))
        dispatch(setAmendHistoryId(null))
        dispatch(pa_clearProposeData())
    }

    const populatedProposalRef = useRef<string | null>(null);
    
    const proposalSignature = useMemo(
        () => (proposal ? JSON.stringify(proposal) : null),
        [proposal],
    );

    useEffect(() => {
        if (!proposal?.application_id || !proposalSignature) {
            return;
        }

        if (populatedProposalRef.current === proposalSignature) {
            return;
        }

        populatedProposalRef.current = proposalSignature;

        repopulateProposeAll();
    }, [
        proposal?.application_id,
        proposalSignature,
        repopulateProposeAll,
    ]);

    const { channelEmail, channelNumber, channelSubName, selectedChannelBox } = useSelector(selectProposeChanelType);
    const showInput =
            selectedChannelBox?.id === CHANNEL_TYPE_ID.broker ||
            selectedChannelBox?.id === CHANNEL_TYPE_ID.generalAgency ||
            selectedChannelBox?.id === CHANNEL_TYPE_ID.agent;
    const proposalAs = useSelector(selectProposeProposalAs);

    const basicInfo = useSelector(selectProposeBasicInformation);
    const affiliations = useSelector(selectProposeAffiliation);
    const natureOfBusinessItem = useSelector(selectProposeNatureOfBusinessItem);
    const companyContactNumber = useSelector(selectProposeCompanyMobileNumber);
    const contactPersonNumber = useSelector(selectProposeContactPersonMobileNumber);
    // GROUP CLASSIFICATION
    const groupClassification = useSelector(selectProposeGroupClassification);
    const hasOtherSpecifyGC = groupClassification?.id === OTHERS_INPUT_IDS.groupClassification
    const otherSpecifGC = useSelector(selectProposeOtherSpecificationGC)
    // TYPE OF GROUP
    const typeOfGroup = useSelector(selectProposeTypeOfGroup)
    const othersTypeOfGroup = useSelector(selectProposeOtherSpecificationToG)
    const typeOfGroupSubtype = useSelector(selectProposeSubType)
    const isEmployerEmployeeToG = typeOfGroup?.id === SUB_TYPE_IDS.employerEmployee
    const isOFWToG = typeOfGroup?.id === SUB_TYPE_IDS.OFW
    const hasSubTypeToG =  isEmployerEmployeeToG || isOFWToG
    // AGE PROFILE
    const ageProfile = useSelector(selectProposeEligibilityAge);
    const ageProfileFormat = ageProfile?.map(a => a.age).join(' - ') || '';
    // MODE OF PAYMENT
    const modeOfPayment = useSelector(selectProposeFundingArrangements);
    // Proposal type (Customize/Prototype)
    const typeOfProposal = useSelector(selectProposeProposalType);
    const isPrototype = typeOfProposal?.id === PROPOSAL_TYPE_IDS.prototype
    const isCustomize = typeOfProposal?.id === PROPOSAL_TYPE_IDS.customized
    // Selected Prototype
    const selectedPrototype = useSelector(selectProposePrototypeType);
    // Product
    const productType = useSelector(selectProposeProductType);
    const isProductGPA = productType?.id === PRODUCT_TYPE_IDS.GPA
    const isProductGCLI = productType?.id === PRODUCT_TYPE_IDS.GCLI
    const isProductGYRT = productType?.id === PRODUCT_TYPE_IDS.GYRT
    const isProductGCI = productType?.id === PRODUCT_TYPE_IDS.GCI
    const isCoverageTypeSelected: boolean =  (isProductGYRT || isProductGPA || isProductGCI) 
    const isAmountOfLoansSelected: boolean =  isProductGCLI 
    // Basic Plan
    const basicPlanType = useSelector(selectProposeBasicPlans);
    // Coverage Type
    const coverageType = useSelector(selectProposeCoverageType);
    const isLevelRanking = coverageType?.id === COVERAGE_TYPE_IDS.levelRanking
    const isUniformCoverage = coverageType?.id === COVERAGE_TYPE_IDS.uniformCoverage
    const isSalaryRank = coverageType?.id === COVERAGE_TYPE_IDS.salaryRank
    const isLevelRankingAndSalaryRank = isLevelRanking || isSalaryRank

    // Level Ranking Coverage
    const levelRankingInputs = useSelector(selectProposeInputsForLevelRanking);
    const levelRankingRiders = useSelector(selectProposeRidersForLevelRanking);
    // Uniform Coverage
    const uniformInputs = useSelector(selectProposeSelectedUniformCoverage);
    const uniformRiders = useSelector(selectProposeRidersForUniform);
    // Salary Rank Coverage
    const salaryRankInputs = useSelector(selectProposeRowsForSalaryRank);
    const salaryRankRiders = useSelector(selectProposeRidersForSalaryRank);
    // Riders 
    const riders = useSelector(selectProposeSelectedRiders);
    const riderBMSR = useSelector(selectProposeSelectedBMSR);

    // Amount of Loans
    const amountOfLoans = useSelector(selectProposeSelectedAmountOfLoans);
    const amountOfLoansRiders = useSelector(selectProposeRidersAmountOfLoans);
    const minAmountOfLoans = useSelector(selectProposeMinAmount);
    const maxAmountOfLoans = useSelector(selectProposeMaxAmount);
    const fAmountMin = formatNumberWithComma(minAmountOfLoans ?? '');
    const fAmountMax = formatNumberWithComma(maxAmountOfLoans ?? '');
    const formatMinMaxAmount = `${fAmountMin} - ${fAmountMax}`;
    const amountOfLoansPortfolio = useSelector(selectProposeLoanPortfolio);
    const fAmountPortfolio = formatNumberWithComma(amountOfLoansPortfolio ?? '');
    // Payment term
    const paymentTerm = useSelector(selectProposePaymentTerm);
    // Maturity Months
    const maturityMonths = useSelector(selectProposeLoanMaturityMonths);
    const maturityMonthsFormat = formatNumberWithComma(maturityMonths ?? '');
    // Commission and Services
    const commission = useSelector(selectProposeCommissionValue);
    const services = useSelector(selectProposeServiceValue);
    // Borrower Age
    const borrowerAgeOptionsList = useSelector(selectProposeBorrowerAgeOptions);
    const minAge = useSelector(selectProposeMinAge);
    const maxAge = useSelector(selectProposeMaxAge);
    // supporting files by GMS
    const files = useSelector(selectProposeFiles);
    // notes
    const notesByCFE = useSelector(selectProposeContent);
    const isPreview = true;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
            <div className="my-auto flex h-[96dvh] w-full flex-col overflow-hidden rounded bg-[#f5f9fd] shadow sm:h-[90dvh] sm:max-w-[95vw] lg:max-w-[700px] xl:max-w-[1000px] 2xl:max-w-[1100px]">
                {isLoadingGetAmendmentHistoryLogs ? (
                    <LoadingState />
                ) : isErrorGetAmendmentHistoryLogs ? (
                    <ErrorState/>
                ) : (
                    <>
                        <div className="flex items-center justify-between border-b border-gray-200 bg-[#f8fafc] px-5 py-3 sm:px-6">
                            <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-center gap-2">
                                    <div className="h-2 w-2 shrink-0 rounded-full bg-[#186cb2]" />
                                    <p className="truncate text-sm font-semibold text-gray-800">
                                        {basicInfo.groupName ?? '-'}
                                    </p>
                                </div>
                                <p className="mt-0.5 truncate pl-4 text-[11px] text-gray-500">
                                    {natureOfBusinessItem?.name ?? '-'}
                                </p>
                            </div>
                            <div className="ml-3 flex shrink-0 items-center">
                                <CloseButtonModal handleClose={handleCloseModal} />
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4 lg:px-6">
                            <div className="space-y-3">
                                <>
                                    <div className="grid grid-cols-1 gap-3.5 ">
                                        <ViewSection
                                            title="Channel & Proposal"
                                            description="Review the selected channel and proposal details."
                                        >
                                            <ChannelTypeReview
                                                selectChannelType={selectedChannelBox}
                                                showInput={showInput}
                                                selectProposalAs={proposalAs?.name}
                                                sECRegisteredNameRenew={''}
                                                isRenew={false}
                                                channelSubName={channelSubName}
                                                channelSubNumber={channelNumber}
                                                channelSubEmail={channelEmail}
                                            />
                                        </ViewSection>
                                        <ViewSection
                                            title="Client Information"
                                            description="Review the client's company and contact information."
                                        >
                                            <BasicInfoReview
                                                info={basicInfo} 
                                                affiliations={affiliations}
                                                natureOfBusinessItem={natureOfBusinessItem?.name}
                                                companyContactNumberName={companyContactNumber?.name}
                                                companyContactNumberType={companyContactNumber?.id}
                                                contactPersonContactName={contactPersonNumber?.name}
                                                contactPersonContactType={contactPersonNumber?.id}
                                            />
                                        </ViewSection>
                                        <ViewSection
                                            title="Group Profile & Payment Details"
                                            description="Review the selected group classification, group type, age profile, and payment arrangement for this proposal."
                                        >
                                            <div className='grid lg:grid-cols-4 gap-2'>
                                                <Selection
                                                    labelTitle='Group Classification'
                                                    labelName={groupClassification?.name ?? ''}
                                                    hasOtherSpecify={hasOtherSpecifyGC}
                                                    otherSpecifyValue={otherSpecifGC ?? ''}
                                                />
                                                <Selection
                                                    labelTitle='Type of Group'
                                                    labelName={typeOfGroup?.name ?? ''}
                                                    hasOtherSpecify={!!othersTypeOfGroup}
                                                    otherSpecifyValue={othersTypeOfGroup ?? ''}
                                                    hasSubType={hasSubTypeToG}
                                                    sabTypeData={typeOfGroupSubtype}
                                                />
                                                <Selection
                                                    labelTitle='Age Profile'
                                                    isHasCheckbox={true}
                                                    labelName={ageProfileFormat}
                                                />
                                                <Selection
                                                    labelTitle='Mode of Payment'
                                                    labelName={modeOfPayment?.name ?? ''}                    
                                                />
                                            </div>
                                        </ViewSection>
                                        <ViewSection
                                            title="Proposal Configuration & Loan Details"
                                            description="Review the proposal type, product and coverage configuration, loan details, payment terms, applicable charges, and required master list file."
                                        >
                                            <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                                <Selection
                                                    labelTitle={`${!typeOfProposal?.name ? '-' : 'Type of Proposal'}`}
                                                    labelName={typeOfProposal?.name ?? ''}                    
                                                />
                                                {isPrototype && (
                                                    <Selection
                                                        labelTitle='Type of Prototype'
                                                        labelName={selectedPrototype?.name ?? ''}                 
                                                    />
                                                )}
                                                {isCustomize && (
                                                    <>
                                                        <Selection
                                                            labelTitle="Type of Product"
                                                            labelName={productType?.name ?? ""}
                                                        />
                                                        <Selection
                                                            labelTitle="Basic Plans"
                                                            labelName={basicPlanType?.name ?? ""}
                                                        />
                                                        {/* Coverage */}
                                                        {isCoverageTypeSelected && (
                                                            <Selection
                                                                labelTitle="Coverage Type"
                                                                labelName={coverageType?.name ?? ""}
                                                            />
                                                        )}
                                                        {/* Amount of Loans */}
                                                        {isAmountOfLoansSelected && (
                                                            <>
                                                                <Selection
                                                                    labelTitle="Amount of Loans"
                                                                    labelName={amountOfLoans?.name ?? ""}
                                                                />
                                                                <Selection
                                                                    labelTitle="Minimum & Maximum Loan Amount"
                                                                    labelName={formatMinMaxAmount}
                                                                />
                                                                <Selection
                                                                    labelTitle="Loan Portfolio"
                                                                    labelName={fAmountPortfolio}
                                                                />
                                                            </>
                                                        )}
                                                        {/* Payment Term */}
                                                        {isAmountOfLoansSelected && (
                                                            <>
                                                                <Selection
                                                                    labelTitle="Payment Term"
                                                                    labelName={paymentTerm?.name ?? ""}
                                                                />
                                                                <Selection
                                                                    labelTitle="Loan Maturity"
                                                                    labelName={`${maturityMonthsFormat} months`}
                                                                />
                                                            </>
                                                        )}
                                                        {/* Commission */}
                                                        <Selection
                                                            labelTitle="Commission Rate"
                                                            labelName={`${commission ?? 0}%`}
                                                        />
                                                        {/* Service Fee */}
                                                        <Selection
                                                            labelTitle="Service/Collection Fee"
                                                            labelName={`${services ?? 0}%`}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </ViewSection>
                                        {/* COVERAGE DETAILS */}
                                        {isCustomize && isCoverageTypeSelected && (
                                            <ViewSection
                                                title="Coverage Configuration"
                                                description="Review how the coverage is structured and applied based on the selected coverage type."
                                            >
                                                <div className="overflow-hidden rounded-lg border border-gray-200">
                                                    {isLevelRanking && (
                                                        <LevelRanking
                                                            inputsByLevelRanking={levelRankingInputs}
                                                            selectedCoverageTypeState={coverageType?.name}
                                                            isPreview={isPreview} 
                                                        />
                                                    )}
                                                    {isUniformCoverage && (
                                                        <UniformCoverage
                                                            inputsByUniformCoverage={uniformInputs}
                                                            selectedCoverageTypeState={coverageType?.name}
                                                            isPreview={isPreview} 
                                                        />
                                                    )}
                                                    {isSalaryRank && (
                                                        <SalaryRank
                                                            inputsBySalaryRank={salaryRankInputs}
                                                            selectedCoverageTypeState={coverageType?.name}
                                                            isPreview={isPreview} 
                                                        />
                                                    )}
                                                </div>
                                            </ViewSection>
                                        )}
                                        {/* ATTACHABLE RIDERS */}
                                        {isCustomize && (
                                            <ViewSection
                                                title="Attachable Riders"
                                                description="Review all selected riders and their corresponding coverage."
                                            >
                                                <div className="overflow-x-auto">
                                                    <SelectedAttachableRiderTable
                                                        isProductGCLI={isProductGCLI}
                                                        isSalaryRank={isSalaryRank}
                                                        isUniformCoverage={isUniformCoverage}
                                                        isLevelRankingAndSalaryRank={isLevelRankingAndSalaryRank}
                                                        isAmountOfLoan={isAmountOfLoansSelected}
                                                        isLevelRanking={isLevelRanking}
                                                        isPreview={isPreview}
                                                        selectedRiders={riders}
                                                        inputsBySalaryRank={salaryRankInputs}
                                                        inputsByLevelRanking={levelRankingInputs}
                                                        inputsByUniformCoverage={uniformInputs}
                                                        selectedCoverageTypeState={coverageType}
                                                        selectedAmountOfLoansCheckBox={amountOfLoans}
                                                        selectedBMSR={riderBMSR}
                                                        amountOfLoansRiders={amountOfLoansRiders}
                                                        selectUniformCoverageRidersState={uniformRiders}
                                                        selectLevelRankingRidersState={levelRankingRiders}
                                                        selectSalaryRankRidersState={salaryRankRiders}
                                                    />
                                                </div>
                                            </ViewSection>
                                        )}
                                        {/* BORROWERS + NOTES */}
                                        {isCustomize && (
                                            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                                {/* Borrowers */}
                                                <ViewSection
                                                    title="Eligibility Age and Benefit Amount"
                                                    description="Review the borrower information and configuration for this proposal."
                                                >
                                                    <ClientInputForBorrowers
                                                        isProductGPA={isProductGPA}
                                                        isProductGCLI={isProductGCLI}
                                                        isProductGYRT={isProductGYRT}
                                                        isProductGCI={isProductGCI}
                                                        borrowerAgeOptionsList={borrowerAgeOptionsList}
                                                        minAge={minAge}
                                                        maxAge={maxAge}
                                                        isPreview={isPreview}
                                                    />
                                                </ViewSection>
                                                {/* Notes */}
                                                <ViewSection
                                                    title="Notes & Remarks"
                                                    description="Review any additional remarks or notes included with this proposal."
                                                >
                                                    <div className="min-h-[180px] rounded-lg border border-gray-200 bg-gray-50 p-4">
                                                        <div className="rich-text-viewer">
                                                            {notesByCFE ? (
                                                                <div
                                                                    // className="ProseMirror break-words text-xs leading-5 text-gray-700"
                                                                    className="text-[13px]"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: notesByCFE,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <p className="text-xs italic text-gray-400">
                                                                    No notes added.
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </ViewSection>
                                            </div>
                                        )}
                                        {isCustomize && (
                                            <ViewSection
                                                title="Supporting Documents"
                                                description="Review the documents attached to this proposal."
                                            >
                                                <ClientSupportingDocuments
                                                    files={files}
                                                    isPreview={isPreview}
                                                />
                                            </ViewSection>
                                        )}
                                    </div>
                                    {/* {isLoadingDownloadMasterAttachedFile && (<LoadingFile/>)} */}
                                </>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
